import {UserRepository} from './UserRepository';
import {UserAPIDataSource} from '../api/UserAPIDataSource';
import {
  DashboardUser,
  CreateUserRequest,
  UpdateUserRequest,
  UpdateUserPasswordRequest,
  AssignRoleRequest,
  RemoveRoleRequest,
  UserFilters,
  UserStatistics
} from '../models/user.model';
import {
  mapUserDTOToModel,
  mapCreateUserRequestToDTO,
  mapUpdateUserRequestToDTO,
  mapUpdatePasswordRequestToDTO,
  mapAssignRoleRequestToDTO,
  mapUsersListResponseToModel,
  mapUserStatisticsDTOToModel
} from '../mappers/user.mapper';
import {AppError} from '../../../Data/config/AppError';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';

export class UserRepositoryImpl implements UserRepository {
  private readonly userDataSource: UserAPIDataSource;

  constructor () {
    this.userDataSource = new UserAPIDataSource();
  }

  // User CRUD Operations
  async createUser (userData: CreateUserRequest): Promise<DashboardUser> {
    try {
      const userDTO = mapCreateUserRequestToDTO(userData);
      const response = await this.userDataSource.createUser(userDTO);
      return mapUserDTOToModel(response);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

      async getUsers (filters?: UserFilters): Promise<{ users: DashboardUser[]; pagination: any }> {
        try {
          const defaultFilters = {
            page: 1,
            limit: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
            ...filters
          };

          const response = await this.userDataSource.getUsers(defaultFilters);
          return mapUsersListResponseToModel(response);
        } catch (error) {
          // Don't handle error here - just throw it so it can be handled at the component level
          if (error instanceof AppError) {
            throw error;
          }
          throw AppError.fromApiError(error);
        }
      }

      async getAdmins (): Promise<{ users: DashboardUser[]; pagination: any }> {
        try {
          const response = await this.userDataSource.getAdmins();
          return mapUsersListResponseToModel(response);
        } catch (error) {
          // Don't handle error here - just throw it so it can be handled at the component level
          if (error instanceof AppError) {
            throw error;
          }
          throw AppError.fromApiError(error);
        }
      }

      async getUserById (id: number): Promise<DashboardUser> {
    try {
      const response = await this.userDataSource.getUserById(id);
      return mapUserDTOToModel(response);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async updateUser (userData: UpdateUserRequest): Promise<DashboardUser> {
    try {
      const userDTO = mapUpdateUserRequestToDTO(userData);
      const response = await this.userDataSource.updateUserProfile(userData.id, userDTO);
      return mapUserDTOToModel(response);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async deleteUser (id: number): Promise<void> {
    try {
      await this.userDataSource.deleteUser(id);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  // User Management Operations
  async updateUserPassword (passwordData: UpdateUserPasswordRequest): Promise<void> {
    try {
      const passwordDTO = mapUpdatePasswordRequestToDTO(passwordData);
      await this.userDataSource.updateUserPassword(passwordData.id, passwordDTO);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async assignRole (roleData: AssignRoleRequest): Promise<DashboardUser> {
    try {
      const roleDTO = mapAssignRoleRequestToDTO(roleData);
      const response = await this.userDataSource.assignRole(roleData.userId, roleDTO);
      return mapUserDTOToModel(response);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async removeRole (roleData: RemoveRoleRequest): Promise<DashboardUser> {
    try {
      await this.userDataSource.removeUserRole(roleData.userId, roleData.roleId);
      // Fetch updated user data after role removal
      const updatedUser = await this.userDataSource.getUserById(roleData.userId);
      return mapUserDTOToModel(updatedUser);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async activateUser (id: number): Promise<DashboardUser> {
    try {
      const response = await this.userDataSource.activateUser(id);
      return mapUserDTOToModel(response);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async deactivateUser (id: number): Promise<DashboardUser> {
    try {
      const response = await this.userDataSource.deactivateUser(id);
      return mapUserDTOToModel(response);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  // Statistics and Analytics
  async getUserStatistics (): Promise<UserStatistics> {
    try {
      const response = await this.userDataSource.getUserStatistics();
      const mappedResponse = mapUserStatisticsDTOToModel(response);
      return mappedResponse;
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  // Permission Management Operations
  async assignUserPermission (userId: number, permissionIds: number[], grantedBy: number): Promise<void> {
    try {
      await this.userDataSource.assignUserPermission(userId, permissionIds, grantedBy);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async bulkAssignRolePermissions (roleId: number, permissionIds: number[], grantedBy: number): Promise<void> {
    try {
      await this.userDataSource.bulkAssignRolePermissions(roleId, permissionIds, grantedBy);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }
}
